/**
 * TNNLogo - inline SVG logo for the Tennessee Nonprofit Network.
 *
 * Visual: stylized "TNN" wordmark with a small Tennessee state outline as
 * a mark to its left. Renders inline so we don't need to ship a separate
 * logo asset file. Color comes from currentColor so it picks up whatever
 * text color the parent specifies (helps on light/dark backgrounds and
 * tenant-branded contexts).
 *
 * Props:
 *   - `variant`: "full" (mark + wordmark) or "mark" (just the TN shape)
 *   - `className`: passed through to the outer SVG; use to size it
 *     (e.g. `className="h-8 w-auto"` for a header-sized logo)
 *
 * Why we built our own rather than ship a real designed logo:
 *   The actual TNN brand mark hasn't been provided. Rather than block on
 *   that, this gives Kevin & Andrea a clean placeholder that matches the
 *   app's visual language. When TNN provides their real mark we swap this
 *   for an <img>/Inline SVG asset and the rest of the codebase doesn't
 *   need to change.
 */

interface TNNLogoProps {
  variant?: "full" | "mark";
  className?: string;
}

/** Inline SVG TNN wordmark; colour follows currentColor. */
export function TNNLogo({ variant = "full", className }: TNNLogoProps) {
  if (variant === "mark") {
    return (
      <svg
        viewBox="0 0 32 32"
        className={className}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="TNN"
      >
        {/* Stylized Tennessee state silhouette - simplified parallelogram nod
            to the actual state shape (TN is famously a long horizontal slab) */}
        <path
          d="M4 11 L28 9 L28 21 L4 23 Z"
          fill="currentColor"
          opacity="0.15"
        />
        <path
          d="M4 11 L28 9 L28 21 L4 23 Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* "TN" inside the state shape */}
        <text
          x="16"
          y="19"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fontSize="9"
          fontWeight="700"
          textAnchor="middle"
          fill="currentColor"
        >
          TN
        </text>
      </svg>
    );
  }

  // Full lockup: mark + wordmark "TNN"
  return (
    <svg
      viewBox="0 0 140 32"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Tennessee Nonprofit Network"
    >
      {/* Mark on the left */}
      <path
        d="M2 11 L26 9 L26 21 L2 23 Z"
        fill="currentColor"
        opacity="0.15"
      />
      <path
        d="M2 11 L26 9 L26 21 L2 23 Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <text
        x="14"
        y="19"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="9"
        fontWeight="700"
        textAnchor="middle"
        fill="currentColor"
      >
        TN
      </text>

      {/* Wordmark "TNN" to the right */}
      <text
        x="36"
        y="22"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="18"
        fontWeight="700"
        letterSpacing="0.02em"
        fill="currentColor"
      >
        TNN
      </text>

      {/* Subtitle: "Operations" - the product name. Smaller, lighter weight. */}
      <text
        x="78"
        y="22"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="11"
        fontWeight="400"
        fill="currentColor"
        opacity="0.6"
      >
        Operations
      </text>
    </svg>
  );
}
